// player/VideoPlayer.tsx — TruXSocial's own video player (1.27.50, owner concepts
// video_watch_page_v2 + shorts_player), shared by the watch page and Flash.
//
//   timeline  — chapters as segments, buffered range, hover frame + time + chapter title
//   controls  — play · ±10 s · volume · time · chapter │ speed · chapters · PiP · theater ·
//               fullscreen · ⋮ settings (Flash: play · time · volume │ speed · fullscreen · ⋮)
//   keyboard  — see playback.ts SHORTCUTS; on the watch page they also work with nothing
//               focused, the way people expect from video sites
//   look      — a restrained violet frame; an optional ambient glow of the video's colours
//
// Views are reported by useViewReporter (3 s of real playback), exactly as before. The
// controls hide after a moment of playing, and stay up while the pointer, keyboard focus,
// a menu or a drag is on them.
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import styled, { css } from 'styled-components';
import type { PostVideo } from '@/core/models/post';
import { Icon, type IconName } from '@/icons';
import { useViewReporter } from '@/components/PostCard/media/useViewReporter';
import { chapterAt, formatClock, type Chapter } from './chapters';
import { Timeline } from './Timeline';
import { pickTrack, useCaptionTracks, useCues } from '../captions/useCaptions';
import { cueAt } from '../captions/vtt';
import { SettingsMenu, type PlaybackInfo } from './SettingsMenu';
import {
  autoWantsHd,
  pickSource,
  renditions,
  SPEED_MAX,
  SPEED_MIN,
  usePlayerPrefs,
  type CaptionSize,
} from './playerPrefs';
import { PlayerPill } from './PlayerPill';
import {
  abJump,
  isTypingTarget,
  shortcutFor,
  type AbRange,
  type RepeatMode,
  type ShortcutAction,
  type SleepChoice,
} from './playback';

const INK = '#fff';
const HIDE_AFTER_MS = 2500;

const Wrap = styled.div<{ $theater: boolean; $flash: boolean }>`
  position: relative;
  isolation: isolate;
  ${({ $theater }) =>
    $theater &&
    css`
      max-height: 80vh;
    `}
`;

const Ambient = styled.canvas`
  position: absolute;
  inset: -18px;
  width: calc(100% + 36px);
  height: calc(100% + 36px);
  z-index: -1;
  filter: blur(42px) saturate(1.35);
  opacity: 0.5;
  pointer-events: none;
  border-radius: 28px;
`;

const Frame = styled.div<{
  $flash: boolean;
  $theater: boolean;
  $cursorHidden: boolean;
  $embed: boolean;
}>`
  position: relative;
  overflow: hidden;
  border-radius: ${({ $flash }) => ($flash ? 18 : 14)}px;
  background: #000;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.4),
    0 0 36px ${({ theme }) => theme.color.accentCyan}2e;
  aspect-ratio: ${({ $flash }) => ($flash ? '9 / 16' : '16 / 9')};
  ${({ $flash }) =>
    $flash &&
    css`
      height: min(calc(100vh - 110px), 860px);
      max-width: 100%;
      margin: 0 auto;
    `}
  ${({ $theater }) =>
    $theater &&
    css`
      max-height: 80vh;
      margin: 0 auto;
    `}
  ${({ $embed }) =>
    $embed &&
    css`
      width: 100vw;
      height: 100vh;
      aspect-ratio: auto;
      border: 0;
      border-radius: 0;
      box-shadow: none;
    `}
  cursor: ${({ $cursorHidden }) => ($cursorHidden ? 'none' : 'default')};
  outline: none;
  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.focusRing};
  }
  &:fullscreen {
    border: 0;
    border-radius: 0;
    aspect-ratio: auto;
    height: 100%;
    max-height: none;
  }
`;

const Video = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  background: #000;
`;

const Scrim = styled.div<{ $show: boolean }>`
  position: absolute;
  inset: auto 0 0 0;
  height: 45%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0));
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 200ms ease;
  pointer-events: none;
`;

const Bottom = styled.div<{ $show: boolean }>`
  position: absolute;
  inset: auto 0 0 0;
  z-index: 4;
  padding: 0 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transform: translateY(${({ $show }) => ($show ? 0 : 8)}px);
  transition:
    opacity 200ms ease,
    transform 200ms ease;
  pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${INK};
`;

const Btn = styled.button<{ $large: boolean }>`
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  width: ${({ $large }) => ($large ? 44 : 36)}px;
  height: ${({ $large }) => ($large ? 44 : 36)}px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: ${INK};
  cursor: pointer;
  transition: background 120ms ease;
  &:hover {
    background: rgba(255, 255, 255, 0.14);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 1px;
  }
  &[aria-pressed='true'] {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const TextBtn = styled(Btn)`
  width: auto;
  min-width: 40px;
  padding: 0 8px;
  font: 700 13px ${({ theme }) => theme.font.body};
  font-variant-numeric: tabular-nums;
  border: 1px solid rgba(255, 255, 255, 0.28);
  height: 30px;
`;

const Time = styled.span`
  padding: 0 6px;
  font: 600 13px ${({ theme }) => theme.font.body};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const ChapterName = styled.span`
  margin-left: 2px;
  font: 500 13px ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.accentCyan};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Spacer = styled.span`
  flex: 1;
  min-width: 4px;
`;

const Volume = styled.div`
  display: flex;
  align-items: center;
  input[type='range'] {
    width: 0;
    opacity: 0;
    transition:
      width 150ms ease,
      opacity 150ms ease;
    accent-color: #fff;
  }
  &:hover input[type='range'],
  &:focus-within input[type='range'],
  &[data-open='true'] input[type='range'] {
    width: 84px;
    opacity: 1;
    margin: 0 6px 0 2px;
  }
`;

// 1.27.60 captions: plain text only (see captions/vtt.ts), lifted clear of the controls.
const CaptionLine = styled.div<{ $raised: boolean; $size: CaptionSize; $bg: boolean }>`
  position: absolute;
  left: 50%;
  bottom: ${({ $raised }) => ($raised ? 92 : 22)}px;
  transform: translateX(-50%);
  z-index: 3;
  max-width: min(86%, 900px);
  pointer-events: none;
  text-align: center;
  white-space: pre-line;
  transition: bottom 200ms ease;
  span {
    display: inline;
    padding: 2px 8px;
    border-radius: 4px;
    line-height: 1.45;
    color: #fff;
    font: 600 ${({ $size }) => ($size === 'small' ? 14 : $size === 'large' ? 26 : 19)}px
      ${({ theme }) => theme.font.body};
    background: ${({ $bg }) => ($bg ? 'rgba(0, 0, 0, 0.72)' : 'transparent')};
    text-shadow: ${({ $bg }) => ($bg ? 'none' : '0 1px 3px #000, 0 0 2px #000')};
    box-decoration-break: clone;
    -webkit-box-decoration-break: clone;
  }
`;

const Center = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  pointer-events: none;
  z-index: 2;
`;

const BigPlay = styled.button`
  pointer-events: auto;
  display: grid;
  place-items: center;
  width: 72px;
  height: 72px;
  padding-left: 4px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(20, 12, 40, 0.55);
  backdrop-filter: blur(6px);
  color: ${INK};
  cursor: pointer;
  transition: transform 140ms ease;
  &:hover {
    transform: scale(1.06);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 3px;
  }
`;

const Bubble = styled.div`
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(10, 8, 18, 0.72);
  color: ${INK};
  font: 700 14px ${({ theme }) => theme.font.body};
  font-variant-numeric: tabular-nums;
`;

const Spin = styled.div`
  color: ${INK};
  animation: spin 900ms linear infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Corner = styled.div<{ $side: 'left' | 'right'; $show: boolean }>`
  position: absolute;
  top: 12px;
  ${({ $side }) => ($side === 'left' ? 'left: 12px;' : 'right: 12px;')}
  z-index: 4;
  display: flex;
  gap: 6px;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 200ms ease;
`;

const ChapterPanel = styled.div`
  position: absolute;
  right: 12px;
  bottom: 64px;
  z-index: 5;
  width: min(320px, calc(100% - 24px));
  max-height: calc(100% - 80px);
  overflow-y: auto;
  padding: 8px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(14, 11, 24, 0.94);
  color: ${INK};
  font: 500 13px ${({ theme }) => theme.font.body};
  h3 {
    margin: 4px 8px 8px;
    font-size: 15px;
  }
  button {
    display: flex;
    gap: 10px;
    width: 100%;
    padding: 8px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${INK};
    font: inherit;
    text-align: left;
    cursor: pointer;
    &:hover {
      background: rgba(255, 255, 255, 0.09);
    }
    &[aria-current='true'] {
      background: rgba(255, 255, 255, 0.14);
      font-weight: 700;
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
    }
  }
  time {
    color: ${({ theme }) => theme.color.accentCyan};
    font-variant-numeric: tabular-nums;
    min-width: 44px;
  }
`;

export interface VideoPlayerHandle {
  seekTo: (seconds: number) => void;
  currentTime: () => number;
  play: () => void;
  pause: () => void;
}

export interface VideoPlayerProps {
  video: PostVideo;
  title: string;
  /** 1.27.60 Clip: play only this section (seconds), on a loop. */
  clipRange?: { start: number; end: number };
  /** 'embed' (1.27.60): fills the whole iframe, no frame or glow — /embed/videos/:id. */
  variant?: 'watch' | 'flash' | 'embed';
  chapters?: Chapter[];
  /** Start here (a shared "?t=" link). */
  startAt?: number;
  autoPlay?: boolean;
  /** Flash loops unless autoplay-next is on. */
  loopByDefault?: boolean;
  theater?: boolean;
  onToggleTheater?: () => void;
  autoplayNext?: boolean;
  onAutoplayNextChange?: (next: boolean) => void;
  onEnded?: () => void;
  onViewCounted?: (views: number) => void;
  onTime?: (seconds: number) => void;
  /** Shortcuts also work when nothing on the page has focus (the watch page). */
  pageShortcuts?: boolean;
  /** Leave ↑/↓ to the page (Flash: they move between Flashes, not the volume). */
  reserveVerticalArrows?: boolean;
  /** Drawn above the controls (Flash: title, creator, description). */
  overlay?: ReactNode;
}

type Feedback = { key: number; text: ReactNode };

export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(function VideoPlayer(
  {
    video,
    title,
    variant = 'watch',
    clipRange,
    chapters = [],
    startAt,
    autoPlay = false,
    loopByDefault = false,
    theater = false,
    onToggleTheater,
    autoplayNext,
    onAutoplayNextChange,
    onEnded,
    onViewCounted,
    onTime,
    pageShortcuts = false,
    reserveVerticalArrows = false,
    overlay,
  },
  ref,
) {
  const flash = variant === 'flash';
  const embed = variant === 'embed';
  const { prefs, set } = usePlayerPrefs();
  const frameRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const ambientRef = useRef<HTMLCanvasElement>(null);
  const menuId = useId();

  const [paused, setPaused] = useState(true);
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(startAt ?? 0);
  const [duration, setDuration] = useState(video.durationMs / 1000);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [pip, setPip] = useState(false);
  const [active, setActive] = useState(true);
  const [menu, setMenu] = useState<'settings' | 'speed' | 'chapters' | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [repeat, setRepeat] = useState<RepeatMode>(loopByDefault ? 'loop' : 'off');
  const [ab, setAb] = useState<AbRange>({ a: null, b: null });
  const [sleep, setSleep] = useState<SleepChoice>('off');
  const [sleepAt, setSleepAt] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [autoHd, setAutoHd] = useState(false);

  const hasHd = video.url !== video.lightUrl;
  const { lightHeight, hdLabel, standardLabel } = renditions(video);
  const source = pickSource(video, prefs.quality, autoHd);
  const src = source === 'full' ? video.url : video.lightUrl;

  // ── Captions (1.27.60) ──────────────────────────────────────────────────────
  const captionTracks = useCaptionTracks(video.id);
  const captionTrack = pickTrack(captionTracks, prefs.captionLang);
  const cues = useCues(video.id, prefs.captionsOn && captionTrack ? captionTrack.language : null);
  const cue = cues ? cueAt(cues, current) : null;

  // ── Clip (1.27.60): stay inside the clipped section, looping back to its start ──
  useEffect(() => {
    if (!clipRange) return;
    const el = videoRef.current;
    if (!el) return;
    if (current >= clipRange.end || current < clipRange.start - 1) {
      el.currentTime = clipRange.start;
    }
  }, [clipRange, current]);

  // Flash follows the Autoplay switch: loop when it is off, move on when it is on.
  useEffect(() => {
    if (!loopByDefault) return;
    setRepeat((r) => (r === 'ab' ? r : autoplayNext ? 'off' : 'loop'));
  }, [loopByDefault, autoplayNext]);

  useViewReporter(videoRef, video.id, onViewCounted);

  // ── "Auto" quality follows the player's size ────────────────────────────────
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setAutoHd(autoWantsHd(el.clientHeight, lightHeight)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [lightHeight]);

  // ── Keep your place across a quality switch ─────────────────────────────────
  const lastTime = useRef(startAt ?? 0);
  const playingRef = useRef(false);
  const resume = useRef<{ t: number; play: boolean } | null>(
    startAt ? { t: startAt, play: autoPlay } : null,
  );
  const firstSrc = useRef(true);
  useEffect(() => {
    if (firstSrc.current) {
      firstSrc.current = false;
      return;
    }
    resume.current = { t: lastTime.current, play: playingRef.current };
  }, [src]);

  // ── Preferences onto the element ────────────────────────────────────────────
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.volume = prefs.volume;
    el.muted = prefs.muted;
  }, [prefs.volume, prefs.muted, src]);
  useEffect(() => {
    const el = videoRef.current as (HTMLVideoElement & { preservesPitch?: boolean }) | null;
    if (!el) return;
    el.playbackRate = prefs.speed;
    el.preservesPitch = prefs.preservePitch;
  }, [prefs.speed, prefs.preservePitch, src]);

  // ── Controls: show on activity, hide while playing ──────────────────────────
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const poke = useCallback(() => {
    setActive(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setActive(false), HIDE_AFTER_MS);
  }, []);
  useEffect(
    () => () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    },
    [],
  );
  const focusInside = () =>
    Boolean(
      frameRef.current?.contains(document.activeElement) &&
      document.activeElement !== frameRef.current,
    );
  const showControls =
    !started ||
    paused ||
    active ||
    menu !== null ||
    scrubbing ||
    prefs.alwaysShowControls ||
    focusInside();

  function say(text: ReactNode) {
    setFeedback({ key: Date.now(), text });
  }
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 700);
    return () => clearTimeout(t);
  }, [feedback]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const play = useCallback(() => {
    const el = videoRef.current;
    if (el) void el.play().catch(() => {});
  }, []);
  const pause = useCallback(() => videoRef.current?.pause(), []);
  const toggle = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused || el.ended) play();
    else pause();
  }, [play, pause]);
  const seekTo = useCallback(
    (t: number) => {
      const el = videoRef.current;
      if (!el) return;
      const d = Number.isFinite(el.duration) ? el.duration : duration;
      el.currentTime = Math.min(Math.max(0, t), Math.max(0, d - 0.05));
      setCurrent(el.currentTime);
    },
    [duration],
  );

  useImperativeHandle(
    ref,
    () => ({
      seekTo,
      currentTime: () => videoRef.current?.currentTime ?? 0,
      play,
      pause,
    }),
    [seekTo, play, pause],
  );

  const toggleFullscreen = useCallback(() => {
    const el = frameRef.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen?.().catch(() => {});
    else void el.requestFullscreen?.().catch(() => {});
  }, []);
  const pipSupported =
    !flash && typeof document !== 'undefined' && Boolean(document.pictureInPictureEnabled);
  const togglePip = useCallback(() => {
    const el = videoRef.current as
      | (HTMLVideoElement & { requestPictureInPicture?: () => Promise<unknown> })
      | null;
    if (!el) return;
    if (document.pictureInPictureElement) void document.exitPictureInPicture?.().catch(() => {});
    else void el.requestPictureInPicture?.().catch(() => {});
  }, []);

  useEffect(() => {
    const onFs = () => setFullscreen(document.fullscreenElement === frameRef.current);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  // React has no picture-in-picture event props — listen on the element.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const on = () => setPip(true);
    const off = () => setPip(false);
    el.addEventListener('enterpictureinpicture', on);
    el.addEventListener('leavepictureinpicture', off);
    return () => {
      el.removeEventListener('enterpictureinpicture', on);
      el.removeEventListener('leavepictureinpicture', off);
    };
  }, []);

  function setVolume(v: number) {
    const volume = Math.min(1, Math.max(0, v));
    set({ volume, muted: volume === 0 });
    say(`${Math.round(volume * 100)}%`);
  }

  const act = useCallback(
    (a: ShortcutAction) => {
      poke();
      const el = videoRef.current;
      switch (a.type) {
        case 'toggle':
          toggle();
          return;
        case 'seekBy':
          seekTo((el?.currentTime ?? 0) + a.seconds);
          say(a.seconds > 0 ? `+${a.seconds} s` : `${a.seconds} s`);
          return;
        case 'seekFraction':
          seekTo(duration * a.fraction);
          return;
        case 'seekTo':
          seekTo(a.where === 'start' ? 0 : duration);
          return;
        case 'volumeBy':
          setVolume(prefs.volume + a.delta);
          return;
        case 'mute':
          set({ muted: !prefs.muted });
          say(prefs.muted ? 'Sound on' : 'Muted');
          return;
        case 'fullscreen':
          toggleFullscreen();
          return;
        case 'theater':
          onToggleTheater?.();
          return;
        case 'pip':
          if (pipSupported) togglePip();
          return;
        case 'speedBy': {
          const speed = Math.min(SPEED_MAX, Math.max(SPEED_MIN, prefs.speed + a.delta));
          set({ speed });
          say(`${Number(speed.toFixed(2))}×`);
          return;
        }
        case 'captions':
          if (!captionTrack) return;
          set({ captionsOn: !prefs.captionsOn });
          say(prefs.captionsOn ? 'Captions off' : `Captions: ${captionTrack.label}`);
          return;
        case 'help':
          setMenu('settings');
          return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      poke,
      toggle,
      seekTo,
      duration,
      prefs,
      set,
      toggleFullscreen,
      onToggleTheater,
      pipSupported,
      togglePip,
      captionTrack,
    ],
  );

  function onFrameKey(e: React.KeyboardEvent) {
    if (isTypingTarget(e.target)) return;
    // Space/Enter on a focused control belongs to that control.
    if ((e.key === ' ' || e.key === 'Enter') && e.target !== frameRef.current) return;
    if (e.key === 'Escape' && menu) {
      setMenu(null);
      return;
    }
    if (reserveVerticalArrows && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) return;
    const a = shortcutFor(e.nativeEvent);
    if (!a) return;
    // Arrow keys on the timeline slider are the slider's own.
    if ((e.target as HTMLElement).getAttribute?.('role') === 'slider' && e.key.startsWith('Arrow'))
      return;
    e.preventDefault();
    act(a);
  }

  useEffect(() => {
    if (!pageShortcuts) return;
    const onKey = (e: KeyboardEvent) => {
      // Only when nothing is focused — never steal keys from a button, link or text box.
      if (e.target !== document.body || isTypingTarget(e.target)) return;
      if (reserveVerticalArrows && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) return;
      const a = shortcutFor(e);
      if (!a) return;
      e.preventDefault();
      act(a);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [pageShortcuts, reserveVerticalArrows, act]);

  // ── Menus close on Escape or a click elsewhere, wherever focus happens to be ───
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element | null;
      if (t?.closest?.('[data-player-menu], [data-player-menu-trigger]')) return;
      setMenu(null);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [menu]);

  // ── Sleep timer ─────────────────────────────────────────────────────────────
  function chooseSleep(choice: SleepChoice) {
    setSleep(choice);
    setSleepAt(typeof choice === 'number' ? Date.now() + choice * 60_000 : null);
  }
  useEffect(() => {
    if (sleepAt === null) return;
    const t = setTimeout(
      () => {
        pause();
        setSleep('off');
        setSleepAt(null);
      },
      Math.max(0, sleepAt - Date.now()),
    );
    return () => clearTimeout(t);
  }, [sleepAt, pause]);

  // ── Ambient glow ────────────────────────────────────────────────────────────
  const drawAmbient = useCallback(() => {
    const c = ambientRef.current;
    const v = videoRef.current;
    const ctx = c?.getContext?.('2d');
    if (!c || !v || !ctx || v.readyState < 2) return;
    try {
      ctx.drawImage(v, 0, 0, c.width, c.height);
    } catch {
      // not decodable yet
    }
  }, []);
  useEffect(() => {
    if (!prefs.ambient || paused || fullscreen) return;
    const t = setInterval(drawAmbient, 400);
    return () => clearInterval(t);
  }, [prefs.ambient, paused, fullscreen, drawAmbient]);

  // ── Element events ──────────────────────────────────────────────────────────
  function onTimeUpdate(e: React.SyntheticEvent<HTMLVideoElement>) {
    const el = e.currentTarget;
    const t = el.currentTime;
    lastTime.current = t;
    setCurrent(t);
    onTime?.(t);
    if (repeat === 'ab') {
      const jump = abJump(ab, t);
      if (jump !== null) el.currentTime = jump;
    }
  }
  function onProgress(e: React.SyntheticEvent<HTMLVideoElement>) {
    const el = e.currentTarget;
    const b = el.buffered;
    let end = 0;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= el.currentTime + 0.5) end = Math.max(end, b.end(i));
    }
    setBufferedEnd(end);
  }
  function onLoadedMetadata(e: React.SyntheticEvent<HTMLVideoElement>) {
    const el = e.currentTarget;
    if (Number.isFinite(el.duration) && el.duration > 0) setDuration(el.duration);
    el.playbackRate = prefs.speed;
    const r = resume.current;
    if (r) {
      resume.current = null;
      el.currentTime = r.t;
      setCurrent(r.t);
      if (r.play) play();
    }
    drawAmbient();
  }
  function handleEnded() {
    if (sleep === 'end') {
      setSleep('off');
      return;
    }
    onEnded?.();
  }

  const nowChapter = chapters.length ? chapterAt(chapters, current) : undefined;
  const large = prefs.largeControls;
  const iconSize = large ? 24 : 20;

  function control(
    label: string,
    icon: IconName,
    onClick: () => void,
    pressed?: boolean,
    menuTrigger = false,
  ) {
    return (
      <Btn
        type="button"
        data-player-menu-trigger={menuTrigger || undefined}
        $large={large}
        aria-label={label}
        title={label}
        aria-pressed={pressed}
        onClick={() => {
          onClick();
          poke();
        }}
      >
        <Icon name={icon} size={iconSize} />
      </Btn>
    );
  }

  const volumeIcon: IconName =
    prefs.muted || prefs.volume === 0 ? 'volume-x' : prefs.volume < 0.5 ? 'volume-1' : 'volume-2';

  const getInfo = useCallback((): PlaybackInfo => {
    const el = videoRef.current;
    const q = (
      el as HTMLVideoElement & { getVideoPlaybackQuality?: () => VideoPlaybackQuality }
    )?.getVideoPlaybackQuality?.();
    const ahead = el ? Math.max(0, bufferedEnd - el.currentTime) : 0;
    const frame = frameRef.current;
    return {
      playing: source === 'full' ? `Full quality (${hdLabel})` : `Light copy (${standardLabel})`,
      resolution: el && el.videoWidth ? `${el.videoWidth} × ${el.videoHeight}` : 'No data',
      duration,
      bufferedAhead: ahead,
      droppedFrames: q ? `${q.droppedVideoFrames} of ${q.totalVideoFrames}` : 'No data',
      viewport: frame ? `${frame.clientWidth} × ${frame.clientHeight}` : 'No data',
      codecs: 'H.264 video, AAC audio',
    };
  }, [bufferedEnd, duration, source, hdLabel, standardLabel]);

  const sleepRemaining = sleepAt !== null ? Math.max(0, (sleepAt - Date.now()) / 1000) : null;

  return (
    <Wrap $theater={theater} $flash={flash} data-testid="video-player-wrap">
      {prefs.ambient && !fullscreen && !embed && (
        <Ambient ref={ambientRef} width={32} height={18} aria-hidden="true" />
      )}
      <Frame
        ref={frameRef}
        $flash={flash}
        $theater={theater}
        $embed={embed}
        $cursorHidden={!showControls && started}
        tabIndex={0}
        role="region"
        aria-label={`Video player: ${title}`}
        data-testid="watch-player"
        onKeyDown={onFrameKey}
        onMouseMove={poke}
        onMouseLeave={() => {
          if (!paused) setActive(false);
        }}
        onFocus={poke}
      >
        <Video
          ref={videoRef}
          src={src}
          poster={video.posterUrl ?? undefined}
          playsInline
          preload="metadata"
          autoPlay={autoPlay}
          loop={repeat === 'loop'}
          aria-label={title}
          style={{
            objectFit: prefs.fit === 'fill' ? 'cover' : 'contain',
            filter:
              prefs.brightness !== 1 || prefs.contrast !== 1
                ? `brightness(${prefs.brightness}) contrast(${prefs.contrast})`
                : undefined,
          }}
          onClick={() => {
            toggle();
            poke();
          }}
          onDoubleClick={toggleFullscreen}
          onPlay={() => {
            setPaused(false);
            setStarted(true);
            playingRef.current = true;
            poke();
          }}
          onPause={() => {
            setPaused(true);
            playingRef.current = false;
          }}
          onWaiting={() => setWaiting(true)}
          onPlaying={() => setWaiting(false)}
          onCanPlay={() => setWaiting(false)}
          onTimeUpdate={onTimeUpdate}
          onProgress={onProgress}
          onLoadedMetadata={onLoadedMetadata}
          onSeeked={drawAmbient}
          onEnded={handleEnded}
        />

        <Center>
          {waiting && !paused ? (
            <Spin aria-hidden="true">
              <Icon name="spinner" size={40} />
            </Spin>
          ) : paused && !scrubbing ? (
            <BigPlay type="button" aria-label="Play" onClick={play}>
              <Icon name="play" size={30} />
            </BigPlay>
          ) : feedback ? (
            <Bubble key={feedback.key} role="status">
              {feedback.text}
            </Bubble>
          ) : null}
        </Center>

        {flash && (
          <>
            <Corner $side="left" $show>
              <PlayerPill
                type="button"
                aria-pressed={!prefs.muted}
                onClick={() => set({ muted: !prefs.muted })}
              >
                <Icon name={prefs.muted ? 'volume-x' : 'volume-2'} size={16} />
                {prefs.muted ? 'Sound off' : 'Sound on'}
              </PlayerPill>
            </Corner>
            <Corner $side="right" $show>
              <PlayerPill as="span">{formatClock(duration)}</PlayerPill>
            </Corner>
          </>
        )}

        {cue && (
          <CaptionLine
            $raised={showControls || Boolean(overlay)}
            $size={prefs.captionSize}
            $bg={prefs.captionBackground}
            data-testid="caption-line"
            aria-live="off"
          >
            <span>{cue.text}</span>
          </CaptionLine>
        )}

        <Scrim $show={showControls || Boolean(overlay)} />
        <Bottom $show={showControls || Boolean(overlay)}>
          {overlay}
          <div style={{ opacity: showControls ? 1 : 0, transition: 'opacity 200ms ease' }}>
            <Timeline
              current={current}
              duration={duration}
              bufferedEnd={bufferedEnd}
              chapters={chapters}
              previewSrc={video.lightUrl}
              large={large}
              onSeek={seekTo}
              onScrubbing={setScrubbing}
            />
            <Bar>
              {control(paused ? 'Play' : 'Pause', paused ? 'play' : 'pause', toggle)}
              {!flash &&
                control('Back 10 seconds', 'rotate-ccw', () =>
                  act({ type: 'seekBy', seconds: -10 }),
                )}
              {!flash &&
                control('Forward 10 seconds', 'rotate-cw', () =>
                  act({ type: 'seekBy', seconds: 10 }),
                )}
              <Volume>
                {control(prefs.muted ? 'Unmute' : 'Mute', volumeIcon, () =>
                  set({ muted: !prefs.muted }),
                )}
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={prefs.muted ? 0 : prefs.volume}
                  aria-label="Volume"
                  onChange={(e) =>
                    set({ volume: Number(e.target.value), muted: Number(e.target.value) === 0 })
                  }
                />
              </Volume>
              <Time data-testid="player-time">
                {formatClock(current)} / {formatClock(duration)}
              </Time>
              {!flash && nowChapter && <ChapterName>{nowChapter.title}</ChapterName>}
              <Spacer />
              {captionTrack &&
                control(
                  prefs.captionsOn ? 'Captions off (c)' : 'Captions on (c)',
                  'captions',
                  () => act({ type: 'captions' }),
                  prefs.captionsOn,
                )}
              <TextBtn
                type="button"
                $large={large}
                data-player-menu-trigger
                aria-label={`Playback speed ${Number(prefs.speed.toFixed(2))}×`}
                aria-haspopup="dialog"
                aria-expanded={menu === 'speed'}
                onClick={() => setMenu(menu === 'speed' ? null : 'speed')}
              >
                {Number(prefs.speed.toFixed(2))}×
              </TextBtn>
              {!flash &&
                chapters.length > 0 &&
                control(
                  'Chapters',
                  'chapters',
                  () => setMenu(menu === 'chapters' ? null : 'chapters'),
                  menu === 'chapters',
                  true,
                )}
              {pipSupported && control('Picture in picture', 'pip', togglePip, pip)}
              {onToggleTheater && control('Theater mode', 'theater', onToggleTheater, theater)}
              {control(
                fullscreen ? 'Exit fullscreen' : 'Fullscreen',
                fullscreen ? 'minimize' : 'maximize',
                toggleFullscreen,
              )}
              <Btn
                type="button"
                $large={large}
                data-player-menu-trigger
                aria-label="Playback settings"
                title="Playback settings"
                aria-haspopup="dialog"
                aria-expanded={menu === 'settings'}
                aria-controls={menu === 'settings' ? menuId : undefined}
                onClick={() => setMenu(menu === 'settings' ? null : 'settings')}
              >
                <Icon name="more-vertical" size={iconSize} />
              </Btn>
            </Bar>
          </div>
        </Bottom>

        {(menu === 'settings' || menu === 'speed') && (
          <SettingsMenu
            key={menu}
            id={menuId}
            startView={menu === 'speed' ? 'speed' : 'main'}
            hasHd={hasHd}
            hdLabel={hdLabel}
            standardLabel={standardLabel}
            autoPlaying={pickSource(video, 'auto', autoHd)}
            repeat={repeat}
            onRepeat={(m) => {
              setRepeat(m);
              if (m !== 'ab') setAb({ a: null, b: null });
            }}
            ab={ab}
            onSetA={() => setAb({ a: videoRef.current?.currentTime ?? 0, b: null })}
            onSetB={() => setAb((r) => ({ ...r, b: videoRef.current?.currentTime ?? null }))}
            sleep={sleep}
            sleepRemaining={sleepRemaining}
            onSleep={chooseSleep}
            autoplayNext={autoplayNext}
            onAutoplayNext={onAutoplayNextChange}
            captionTracks={captionTracks ?? []}
            captionTrack={captionTrack}
            getInfo={getInfo}
            onClose={() => {
              setMenu(null);
              frameRef.current?.focus();
            }}
          />
        )}

        {menu === 'chapters' && (
          <ChapterPanel
            data-player-menu
            role="dialog"
            aria-label="Chapters"
            onKeyDown={(e) => e.key === 'Escape' && setMenu(null)}
          >
            <h3>Chapters</h3>
            {chapters.map((c) => (
              <button
                key={c.start}
                type="button"
                aria-current={nowChapter === c}
                onClick={() => {
                  seekTo(c.start);
                  setMenu(null);
                }}
              >
                <time>{formatClock(c.start)}</time>
                <span>{c.title}</span>
              </button>
            ))}
          </ChapterPanel>
        )}
      </Frame>
    </Wrap>
  );
});
